import { useState, useCallback } from 'react';
import { MessageFeedback } from '../types';
import { feedbackService, ConversationMessage, ToolCallDetail } from '../services/feedbackService';

interface UseFeedbackReturn {
  messageFeedbacks: Map<string, MessageFeedback>;
  submitFeedback: (
    messageId: string,
    feedback: 'positive' | 'negative',
    agentName: string,
    userMessage: string,
    agentMessage: string,
    userEmail: string,
    messageCount: number,
    toolCalls?: string[],
    a2aRouting?: {
      originalAgent: string;
      currentAgent: string;
      previousAgent?: string;
      reason?: string;
    },
    feedbackTags?: string[],
    feedbackNotes?: string,
    // NEW ENHANCED PARAMETERS
    conversationHistory?: ConversationMessage[],
    toolCallsDetailed?: ToolCallDetail[],
    metadata?: Record<string, any>,
    allowUpdate?: boolean
  ) => Promise<boolean>;
  getFeedbackForMessage: (messageId: string) => MessageFeedback | null;
  isFeedbackSubmitted: (messageId: string) => boolean;
  clearFeedbacks: () => void;
}

export const useFeedback = (): UseFeedbackReturn => {
  const [messageFeedbacks, setMessageFeedbacks] = useState<Map<string, MessageFeedback>>(new Map());

  // Submit feedback for a message (ENHANCED VERSION)
  const submitFeedback = useCallback(async (
    messageId: string,
    feedback: 'positive' | 'negative',
    agentName: string,
    userMessage: string,
    agentMessage: string,
    userEmail: string,
    messageCount: number,
    toolCalls?: string[],
    a2aRouting?: {
      originalAgent: string;
      currentAgent: string;
      previousAgent?: string;
      reason?: string;
    },
    feedbackTags?: string[],
    feedbackNotes?: string,
    // NEW ENHANCED PARAMETERS
    conversationHistory?: ConversationMessage[],
    toolCallsDetailed?: ToolCallDetail[],
    metadata?: Record<string, any>,
    allowUpdate?: boolean
  ): Promise<boolean> => {
    try {
      // Check if feedback already exists for this message
      const existingFeedback = messageFeedbacks.get(messageId);
      if (existingFeedback?.isSubmitted && !allowUpdate) {
        console.warn('Feedback already submitted for message:', messageId);
        return false;
      }

      // Update local state immediately for UI feedback
      const newFeedback: MessageFeedback = {
        messageId,
        feedback,
        isSubmitted: false
      };

      setMessageFeedbacks(prev => new Map(prev).set(messageId, newFeedback));

      // Submit to enhanced feedback service
      const success = await feedbackService.addFeedback(
        messageId,
        agentName,
        userMessage,
        agentMessage,
        feedback,
        userEmail,
        messageCount,
        toolCalls,
        a2aRouting,
        feedbackTags,
        feedbackNotes,
        conversationHistory,    // NEW
        toolCallsDetailed,      // NEW
        metadata                // NEW: Generic metadata
      );

      // Update submission status
      setMessageFeedbacks(prev => {
        const updated = new Map(prev);
        const current = updated.get(messageId);
        if (current) {
          updated.set(messageId, {
            ...current,
            isSubmitted: success
          });
        }
        return updated;
      });

      return success;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      
      // Remove from local state on error
      setMessageFeedbacks(prev => {
        const updated = new Map(prev);
        updated.delete(messageId);
        return updated;
      });
      
      return false;
    }
  }, [messageFeedbacks]);

  // Get feedback for a specific message
  const getFeedbackForMessage = useCallback((messageId: string): MessageFeedback | null => {
    return messageFeedbacks.get(messageId) || null;
  }, [messageFeedbacks]);

  // Check if feedback has been submitted for a message
  const isFeedbackSubmitted = useCallback((messageId: string): boolean => {
    const feedback = messageFeedbacks.get(messageId);
    return feedback?.isSubmitted || false;
  }, [messageFeedbacks]);

  // Clear all feedbacks (e.g., when starting new conversation)
  const clearFeedbacks = useCallback(() => {
    setMessageFeedbacks(new Map());
  }, []);

  return {
    messageFeedbacks,
    submitFeedback,
    getFeedbackForMessage,
    isFeedbackSubmitted,
    clearFeedbacks
  };
}; 